import React, { useState, useEffect, useImperativeHandle, forwardRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRotate, faFilePen, faDollarSign, faExclamationTriangle, faBan } from '@fortawesome/free-solid-svg-icons';
import { accountApi } from '../api/accountApi';
import { schwabApi } from '../api/schwabApi';
import { useAuth } from '../contexts/AuthContext';

function maskAccount(num) {
  if (!num) return 'All accounts';
  const s = String(num);
  if (s.length <= 4) return s;
  return `••••${s.slice(-4)}`;
}

const PL_PRESETS = [
  { value: 'today', label: 'Today' },
  { value: 'ytd', label: 'YTD' },
  { value: '1w', label: '1W' },
  { value: '1m', label: '1M' },
  { value: '3m', label: '3M' },
  { value: '1y', label: '1Y' },
  { value: 'all', label: 'All' },
  { value: 'custom', label: 'Custom' },
];

const AccountSummary = forwardRef(({
  venue = 'alpaca',
  onVenueChange,
  schwabAccount = '',
  onSchwabAccountChange,
}, ref) => {
  const { maskAmounts } = useAuth();
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [positions, setPositions] = useState([]);
  const [schwabSummary, setSchwabSummary] = useState(null);
  const [schwabConnected, setSchwabConnected] = useState(null);

  const [plPreset, setPlPreset] = useState('today');
  const [customStart, setCustomStart] = useState(() => `${new Date().getFullYear()}-01-01`);
  const [customEnd, setCustomEnd] = useState(() => new Date().toISOString().slice(0, 10));
  const [periodPl, setPeriodPl] = useState(null);
  const [periodPlLoading, setPeriodPlLoading] = useState(false);
  const [periodPlError, setPeriodPlError] = useState(null);

  const loadPeriodPl = useCallback(async (preset, start, end) => {
    try {
      setPeriodPlLoading(true);
      setPeriodPlError(null);
      const opts = { preset };
      if (preset === 'custom') {
        opts.start = start;
        opts.end = end || undefined;
      }
      const res = await accountApi.getPeriodPl(opts);
      setPeriodPl(res.data || null);
    } catch (err) {
      setPeriodPl(null);
      setPeriodPlError(err.response?.data?.error || err.message || 'Failed to load period P/L');
    } finally {
      setPeriodPlLoading(false);
    }
  }, []);

  const loadAccount = useCallback(async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      if (venue === 'schwab') {
        const statusRes = await schwabApi.getStatus();
        const connected = !!statusRes.data?.connected;
        setSchwabConnected(connected);
        if (!connected) {
          setSchwabSummary(null);
          setError(null);
          return;
        }
        const summaryRes = await schwabApi.getSummary();
        setSchwabSummary(summaryRes.data || null);
        setError(null);
        return;
      }

      const [acctRes, summaryRes, positionsRes] = await Promise.all([
        accountApi.getAccount(),
        accountApi.getBalanceSummary().catch(() => null),
        accountApi.getPositions().catch(() => ({ data: [] })),
      ]);
      setAccount(acctRes.data);
      setSummary(summaryRes?.data || null);
      setPositions(positionsRes.data || []);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [venue]);

  useImperativeHandle(ref, () => ({
    refresh: () => {
      loadAccount(false);
      if (venue !== 'schwab') loadPeriodPl(plPreset, customStart, customEnd);
    },
  }));

  useEffect(() => {
    loadAccount();
  }, [loadAccount]);

  useEffect(() => {
    if (venue === 'schwab') return;
    if (plPreset === 'custom' && !customStart) return;
    loadPeriodPl(plPreset, customStart, customEnd);
  }, [venue, plPreset, customStart, customEnd, loadPeriodPl]);

  const formatCurrency = (value) => {
    if (maskAmounts) return '***';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: account?.currency || 'USD',
    }).format(value);
  };

  const renderDelta = (change, pct) => {
    if (change === null || change === undefined) {
      return <span className="text-xs text-slate-400">–</span>;
    }
    const positive = change >= 0;
    return (
      <div className={`num text-xs mt-0.5 font-medium ${positive ? 'text-bull' : 'text-bear'}`}>
        {positive ? '+' : ''}
        {formatCurrency(change)}{' '}
        {pct !== null && pct !== undefined ? `(${positive ? '+' : ''}${pct.toFixed(2)}%)` : ''}
      </div>
    );
  };

  const venueSwitch = (
    <div className="inline-flex rounded-md border border-hairline overflow-hidden text-[11px]">
      <button
        type="button"
        onClick={() => onVenueChange?.('alpaca')}
        className={`px-2.5 py-1 font-semibold ${
          venue === 'alpaca' ? 'bg-action text-white' : 'text-muted hover:bg-elevated'
        }`}
      >
        Alpaca
      </button>
      <button
        type="button"
        onClick={() => onVenueChange?.('schwab')}
        className={`px-2.5 py-1 font-semibold ${
          venue === 'schwab' ? 'bg-action text-white' : 'text-muted hover:bg-elevated'
        }`}
      >
        Schwab
      </button>
    </div>
  );

  if (loading && ((venue === 'schwab' && schwabConnected == null) || (venue !== 'schwab' && !account))) {
    return (
      <div className="lt-card p-6">
        <div className="animate-pulse">
          <div className="h-3 bg-elevated rounded w-24 mb-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-20 bg-elevated rounded-md" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6">
        <div className="flex items-center justify-between gap-3 mb-2">{venueSwitch}</div>
        <p className="text-red-800">Error: {error}</p>
      </div>
    );
  }

  if (venue === 'schwab') {
    if (!schwabConnected) {
      return (
        <div className="lt-card p-4">
          <div className="flex items-center justify-between gap-3 mb-3">
            <div className="lt-label">Account equity</div>
            {venueSwitch}
          </div>
          <p className="text-sm text-muted">
            Schwab is not connected.{' '}
            <Link to="/schwab" className="text-action font-medium hover:underline">
              Connect Schwab
            </Link>
          </p>
        </div>
      );
    }

    const accounts = schwabSummary?.accounts || [];
    const selected = schwabAccount
      ? accounts.find((a) => String(a.accountNumber) === String(schwabAccount))
      : null;
    const equity = Number(selected?.equity ?? schwabSummary?.equity) || 0;
    const cash = Number(selected?.cash ?? schwabSummary?.cash) || 0;
    const buyingPower = Number(selected?.buyingPower ?? schwabSummary?.buyingPower) || 0;
    const dayPl = Number(selected?.dayPl ?? schwabSummary?.dayPl);
    const lastEquity = Number(selected?.lastEquity ?? schwabSummary?.lastEquity) || 0;
    const dayPct = lastEquity > 0 && Number.isFinite(dayPl) ? (dayPl / lastEquity) * 100 : null;
    const money = (value) => {
      if (maskAmounts) return '***';
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value || 0);
    };

    return (
      <div className="lt-card p-4">
        <div className="flex items-end justify-between mb-4 gap-3 flex-wrap">
          <div>
            <div className="lt-label mb-1">Account equity</div>
            <div className="num text-[28px] font-semibold tracking-tight text-ink leading-none">
              {money(equity)}
            </div>
            {Number.isFinite(dayPl) && (
              <div className="flex items-baseline gap-1.5">
                {renderDelta(dayPl, dayPct)}
                <span className="text-[10px] font-medium uppercase tracking-[0.12em] text-muted">
                  Today
                </span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            {venueSwitch}
            <select
              value={schwabAccount}
              onChange={(e) => onSchwabAccountChange?.(e.target.value)}
              className="h-8 px-2 rounded-md border border-hairline bg-canvas text-xs text-ink"
              aria-label="Schwab account"
            >
              <option value="">All accounts</option>
              {accounts.map((acct) => (
                <option key={acct.accountNumber} value={acct.accountNumber}>
                  {(acct.accountType || 'Account')} {maskAccount(acct.accountNumber)}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => loadAccount()}
              className="h-8 px-3 rounded-md bg-action text-white hover:opacity-90 transition-opacity text-xs font-semibold"
            >
              <FontAwesomeIcon icon={faRotate} className="mr-1 text-xs" />
              Refresh
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
          <div className="rounded-md p-3 border border-hairline bg-canvas">
            <div className="lt-label mb-1">Today P/L</div>
            <div className={`num text-[16px] font-medium ${dayPl >= 0 ? 'text-bull' : 'text-bear'}`}>
              {Number.isFinite(dayPl) ? `${dayPl >= 0 ? '+' : ''}${money(dayPl)}` : '—'}
            </div>
          </div>
          <div className="rounded-md p-3 border border-hairline bg-canvas">
            <div className="lt-label mb-1">Buying power</div>
            <div className="num text-[16px] font-medium text-ink">{money(buyingPower)}</div>
          </div>
          <div className="rounded-md p-3 border border-hairline bg-canvas">
            <div className="lt-label mb-1">Cash</div>
            <div className="num text-[16px] font-medium text-ink">{money(cash)}</div>
          </div>
          <div className="rounded-md p-3 border border-hairline bg-canvas">
            <div className="lt-label mb-1">Positions</div>
            <div className="num text-[16px] font-medium text-ink">
              {selected ? '—' : schwabSummary?.positionCount ?? '—'}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!account && !loading) return null;

  const equity = parseFloat(account.equity) || 0;
  const lastEquity = parseFloat(account.lastEquity) || 0;
  const dayPlFromAccount = lastEquity > 0 ? equity - lastEquity : null;
  const dayPlFromPositions = positions.reduce((sum, pos) => {
    const intraday = parseFloat(pos.unrealizedIntradayPl);
    if (!Number.isNaN(intraday) && pos.unrealizedIntradayPl != null) return sum + (intraday || 0);
    const qty = Math.abs(parseFloat(pos.qty) || 0);
    const changePerShare = parseFloat(pos.changeToday) || 0;
    const isShort = pos.side?.toLowerCase() === 'short' || pos.side?.toLowerCase() === 'sell';
    return sum + qty * changePerShare * (isShort ? -1 : 1);
  }, 0);
  const dayPlFallback =
    dayPlFromAccount != null
      ? dayPlFromAccount
      : summary?.change != null
        ? summary.change
        : positions.length > 0
          ? dayPlFromPositions
          : null;
  const dayPlPctFallback =
    dayPlFromAccount != null && lastEquity > 0
      ? (dayPlFromAccount / lastEquity) * 100
      : summary?.changePct != null
        ? summary.changePct
        : equity > 0 && dayPlFallback != null
          ? (dayPlFallback / equity) * 100
          : null;

  // Prefer API period P/L; for today fall back to local calc if history is slow/empty
  const displayPl =
    periodPl?.pl != null
      ? periodPl.pl
      : plPreset === 'today'
        ? dayPlFallback
        : null;

  // Backend plPct is a fraction (0.05 = 5%); local today fallback is already percent
  let plPctDisplay = null;
  if (periodPl?.plPct != null && Number.isFinite(periodPl.plPct)) {
    plPctDisplay = Math.abs(periodPl.plPct) <= 2 ? periodPl.plPct * 100 : periodPl.plPct;
  } else if (plPreset === 'today' && dayPlPctFallback != null) {
    plPctDisplay = dayPlPctFallback;
  }

  const periodLabel =
    PL_PRESETS.find((p) => p.value === plPreset)?.label || periodPl?.label || 'Period';

  return (
    <div className="lt-card p-4">
      <div className="flex items-end justify-between mb-4 gap-3">
        <div>
          <div className="lt-label mb-1">Account equity</div>
          <div className="num text-[28px] font-semibold tracking-tight text-ink leading-none">
            {formatCurrency(account.equity)}
          </div>
          {displayPl != null && (
            <div className="flex items-baseline gap-1.5">
              {renderDelta(displayPl, plPctDisplay)}
              <span className="text-[10px] font-medium uppercase tracking-[0.12em] text-muted">
                {periodLabel}
              </span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {venueSwitch}
          <span
            className={`px-2 py-1 rounded-md text-[10px] font-semibold uppercase tracking-wider border ${
              account.environment === 'paper'
                ? 'border-[rgba(232,184,74,0.35)] text-[var(--lt-warning)]'
                : 'border-[rgba(61,220,151,0.35)] text-bull'
            }`}
          >
            <FontAwesomeIcon
              icon={account.environment === 'paper' ? faFilePen : faDollarSign}
              className="mr-1 text-[10px]"
            />
            {account.environment === 'paper' ? 'Paper' : 'Live'}
          </span>
          <button
            type="button"
            onClick={() => {
              loadAccount();
              loadPeriodPl(plPreset, customStart, customEnd);
            }}
            className="h-8 px-3 rounded-md bg-action text-white hover:opacity-90 transition-opacity text-xs font-semibold"
          >
            <FontAwesomeIcon icon={faRotate} className="mr-1 text-xs" />
            Refresh
          </button>
        </div>
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <span className="lt-label">P/L period</span>
        <div className="flex flex-wrap gap-1">
          {PL_PRESETS.map((p) => (
            <button
              key={p.value}
              type="button"
              onClick={() => setPlPreset(p.value)}
              className={`h-7 px-2 rounded-md text-[10px] font-semibold uppercase tracking-wide border transition-colors ${
                plPreset === p.value
                  ? 'bg-action text-white border-transparent'
                  : 'bg-canvas border-hairline text-muted hover:text-ink'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        {plPreset === 'custom' && (
          <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
            <input
              type="date"
              value={customStart}
              onChange={(e) => setCustomStart(e.target.value)}
              className="h-7 rounded-md border border-hairline bg-canvas px-1.5 text-ink"
            />
            <span className="text-muted">→</span>
            <input
              type="date"
              value={customEnd}
              onChange={(e) => setCustomEnd(e.target.value)}
              className="h-7 rounded-md border border-hairline bg-canvas px-1.5 text-ink"
            />
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
        <div className="rounded-md p-3 border border-hairline bg-canvas">
          <div className="lt-label mb-1 flex items-center gap-1.5">
            {periodLabel} P/L
            {periodPlLoading && (
              <span className="text-[9px] text-muted font-normal normal-case tracking-normal">
                loading…
              </span>
            )}
          </div>
          <div
            className={`num text-[16px] font-medium ${
              displayPl == null ? 'text-ink' : displayPl >= 0 ? 'text-bull' : 'text-bear'
            }`}
          >
            {displayPl == null ? '—' : `${displayPl >= 0 ? '+' : ''}${formatCurrency(displayPl)}`}
          </div>
          {plPctDisplay != null && (
            <div
              className={`num text-xs mt-0.5 font-medium ${
                plPctDisplay >= 0 ? 'text-bull' : 'text-bear'
              }`}
            >
              {plPctDisplay >= 0 ? '+' : ''}
              {plPctDisplay.toFixed(2)}%
            </div>
          )}
          {periodPlError && (
            <div className="text-[10px] text-bear mt-1 leading-snug">{periodPlError}</div>
          )}
          {periodPl?.startEquity != null && periodPl?.endEquity != null && !periodPlLoading && (
            <div className="num text-[10px] text-muted mt-1">
              {formatCurrency(periodPl.startEquity)} → {formatCurrency(periodPl.endEquity)}
            </div>
          )}
        </div>

        <div className="rounded-md p-3 border border-hairline bg-canvas">
          <div className="lt-label mb-1">Buying power</div>
          <div className="num text-[16px] font-medium text-ink">
            {formatCurrency(account.buyingPower)}
          </div>
        </div>

        <div className="rounded-md p-3 border border-hairline bg-canvas">
          <div className="lt-label mb-1">Cash</div>
          <div className="num text-[16px] font-medium text-ink">{formatCurrency(account.cash)}</div>
        </div>

        <div className="rounded-md p-3 border border-hairline bg-canvas">
          <div className="lt-label mb-1">Portfolio value</div>
          <div className="num text-[16px] font-medium text-ink">
            {formatCurrency(account.portfolioValue)}
          </div>
        </div>

        {account.dayTradingBuyingPower > 0 && (
          <div className="rounded-md p-3 border border-hairline bg-canvas">
            <div className="lt-label mb-1">Day trading BP</div>
            <div className="num text-[16px] font-medium text-ink">
              {formatCurrency(account.dayTradingBuyingPower)}
            </div>
          </div>
        )}
      </div>

      {account.patternDayTrader && (
        <div className="mt-3 bg-yellow-50 border-l-4 border-yellow-400 p-2 rounded">
          <div className="flex items-center">
            <FontAwesomeIcon icon={faExclamationTriangle} className="text-yellow-600 mr-1.5 text-xs" />
            <p className="text-yellow-800 text-xs">
              <strong>Pattern Day Trader (PDT) Flag Active</strong> — Account must maintain $25,000
              minimum equity
            </p>
          </div>
        </div>
      )}

      {(account.tradingBlocked || account.accountBlocked) && (
        <div className="mt-3 bg-red-50 border-l-4 border-red-400 p-2 rounded">
          <div className="flex items-center">
            <FontAwesomeIcon icon={faBan} className="text-red-600 mr-1.5 text-xs" />
            <p className="text-red-800 text-xs">
              <strong>Account or Trading is Blocked</strong> — Please contact Alpaca support
            </p>
          </div>
        </div>
      )}

      <div className="mt-3 pt-3 border-t border-hairline">
        <span className="lt-label mr-2">Status</span>
        <span
          className={`px-1.5 py-0.5 rounded-sm text-[10px] font-semibold uppercase tracking-wider ${
            account.status === 'ACTIVE'
              ? 'bg-[var(--lt-bull-dim)] text-bull'
              : 'bg-elevated text-muted'
          }`}
        >
          {account.status}
        </span>
        <span className="text-[10px] text-muted ml-2">
          Period P/L uses Alpaca portfolio history (deposits/withdrawals also move equity)
        </span>
      </div>
    </div>
  );
});

AccountSummary.displayName = 'AccountSummary';

export default AccountSummary;
